/**
 * G4 Submit & Reevaluate auto-recovery loop (extracted from
 * ``ContentAwareG4Gate.tsx`` so the retry budget can be pinned by a
 * unit test — Bug #25 [v3]).
 *
 * The Q9 backend re-derives the G4 panel from a fresh ``run_critic``
 * pass when the submission lands, so the option ``enabled`` flags can
 * drift from the cached payload (e.g. the Rewriter's best attempt
 * didn't converge on this run, so ``accept_rewrite`` is now
 * disabled). When that happens the resolver returns 422
 * ``g4.invalid_submission`` with ``offending_finding_ids`` — flip
 * those rows to ``accept_original`` (a safe terminal pick that logs
 * the finding to accepted-violations.json) and retry before bubbling
 * the error up to the architect.
 */
import { ApiError } from '@/lib/api'
import {
  BULK_ACCEPT_RATIONALE,
  BULK_BATCH_RATIONALE,
  readQ9Payload,
  type Q9Payload,
} from '@/lib/g4Sanitiser'
import type { GateDecisionRecord } from '@/types'

export interface SubmitWithRecoveryOptions {
  /** Initial submission payload — typically the output of
   * ``sanitiseQ9Submission`` so W3.1 + W3.2 are already satisfied on
   * the happy path. */
  initialAttempt: GateDecisionRecord[]
  /** Number of recovery retries after the initial attempt. Total
   * network calls in the worst case = 1 + ``maxRetries``. Reads from
   * ``settings.max_submit_retries`` in production (Bug #21 [v2]). */
  maxRetries: number
  /** W3.2 threshold for ``batch_rationale`` injection after a flip.
   * Reads from ``settings.bulk_accept_threshold`` (Bug #21 [v2]). */
  bulkAcceptThreshold: number
  /** Network-call callback. Throws ``ApiError`` on non-2xx. The
   * return value is ignored — only success/throw matters here.
   * Decoupled from ``api.submitGateDecisions`` so the test can mock
   * it directly. */
  submit: (attempt: GateDecisionRecord[]) => Promise<unknown>
}

export interface SubmitWithRecoveryResult {
  /** Final payload that the server accepted. May differ from
   * ``initialAttempt`` if any rows were flipped to
   * ``accept_original`` during recovery. */
  updated: GateDecisionRecord[]
  /** Number of rows that the loop flipped to ``accept_original``
   * across all retry passes. ``> 0`` means the call site should
   * surface the "Auto-recovered flagged rewrites" toast. */
  totalRecovered: number
}

/**
 * Pull the ``offending_finding_ids`` array out of an ApiError's
 * ``error.details`` envelope. The backend nests the field under
 * ``error.details.offending_finding_ids`` (see
 * ``api/routes/gates.py``) — this helper unwraps both wrappers and
 * filters non-strings. Returns ``[]`` when the field is missing or
 * malformed so callers fall back to surfacing the original error.
 */
export function extractOffendingFindingIds(err: ApiError): string[] {
  const details = (err.detail as { details?: unknown } | undefined)?.details
  if (!details || typeof details !== 'object') return []
  const ids = (details as { offending_finding_ids?: unknown }).offending_finding_ids
  if (!Array.isArray(ids)) return []
  return ids.filter((id): id is string => typeof id === 'string')
}

/**
 * Flip a Q9 ``F-###`` decision to the safe ``accept_original``
 * choice. The backend logs the finding to
 * ``accepted-violations.json`` (so the architect's intent is
 * recorded for the audit trail) and excludes it from the export
 * bundle, which is the right behaviour when the Rewriter's best
 * attempt is no longer convergent on this run.
 *
 * Falls back to a status-only update for non-Q9 decisions (legacy
 * REW-### rows, edge-case placeholder ids) so the helper is safe to
 * call uniformly across the submission set.
 */
export function coerceToAcceptOriginal(
  decision: GateDecisionRecord,
): GateDecisionRecord {
  const q9 = readQ9Payload(decision)
  if (!q9) {
    return { ...decision, status: 'approved' }
  }
  // W3.1 (Hardening) — the backend's rationale-floor validator only
  // fires on critical / high severity findings. To keep the audit
  // trail consistent (and to be defensive against severity-flips on
  // re-emit) we always seed a default rationale here when none was
  // typed. Without this Submit & Reevaluate would loop forever on a
  // panel that's already on ``accept_original`` with an empty
  // rationale — the original failure mode the architect reported.
  const existingRationale = (q9.choice?.rationale ?? '').trim()
  const seededRationale =
    existingRationale.length === 0
      ? BULK_ACCEPT_RATIONALE
      : (q9.choice?.rationale ?? '')
  const nextChoice: Q9Payload['choice'] = {
    option_id: 'accept_original',
    manual_edit_text: '',
    reprompt_note: '',
    rationale: seededRationale,
  }
  const nextPayload = {
    ...((decision.payload as Record<string, unknown>) ?? {}),
    choice: nextChoice,
  }
  return {
    ...decision,
    status: 'approved',
    answer: 'accept_original',
    payload: nextPayload,
  }
}

/**
 * Submit a G4 decision payload, recovering from W3.x 422 rejections
 * by flipping the ``offending_finding_ids`` to ``accept_original``
 * and re-submitting.
 *
 * **Retry budget (Bug #25 [v3]).** Run the initial attempt + up to
 * ``maxRetries`` recovery passes, for a total of ``1 + maxRetries``
 * network calls in the worst case. With the default
 * ``settings.max_submit_retries = 3`` the worst case is 4 calls.
 * Each pass after the first flips the offending rows from the
 * previous 422 and re-submits — e.g. the first 422 reports
 * F-039/F-040, we flip those, the re-derived panel surfaces F-024
 * next, etc. The bound keeps a degenerate validator (same offending
 * list returned every time) from hanging the UI.
 *
 * **Loop counter.** ``retry`` ranges ``0..maxRetries`` inclusive, so
 * the body executes ``maxRetries + 1`` times. The throw at
 * ``retry === maxRetries`` ensures the final iteration never tries
 * to send a (maxRetries + 2)th request.
 *
 * **Progress predicate.** A row only counts as "recovered" when
 * ``coerceToAcceptOriginal`` actually mutates its ``option_id`` or
 * ``rationale``. Without this guard a degenerate validator returning
 * the same offending list every time would never make real progress
 * and we'd burn the retry budget for nothing — surface the original
 * error instead so the architect can decide.
 *
 * **W3.2 priming.** When the post-flip ``accept_original`` count
 * exceeds ``bulkAcceptThreshold``, attach ``batch_rationale`` to the
 * first decision payload so the bulk-accept validator passes on the
 * very next retry.
 *
 * Side-effect-free: the success toast is the caller's
 * responsibility (it reads ``result.totalRecovered > 0``).
 */
export async function submitWithRecovery({
  initialAttempt,
  maxRetries,
  bulkAcceptThreshold,
  submit,
}: SubmitWithRecoveryOptions): Promise<SubmitWithRecoveryResult> {
  let attempt = initialAttempt
  let totalRecovered = 0
  for (let retry = 0; retry <= maxRetries; retry += 1) {
    try {
      await submit(attempt)
      return { updated: attempt, totalRecovered }
    } catch (err) {
      const recoverable =
        err instanceof ApiError &&
        err.status === 422 &&
        err.code === 'g4.invalid_submission'
      if (!recoverable || retry === maxRetries) throw err
      const offending = extractOffendingFindingIds(err as ApiError)
      // If the backend gives us no actionable offending list, bail —
      // there's nothing we can mutate.
      if (offending.length === 0) throw err
      // Two distinct 422 causes we recover from here:
      //   (1) ``accept_rewrite`` is now disabled (Critic drift —
      //       the option_id needs to flip to ``accept_original``).
      //   (2) ``accept_original`` on a critical/high finding has
      //       an empty or sub-floor rationale (W3.1) — the
      //       option_id is already right, but the rationale
      //       needs the bulk-default seed.
      const flipped = new Set<string>()
      attempt = attempt.map((d) => {
        if (!offending.includes(d.id)) return d
        const before = readQ9Payload(d)
        const next = coerceToAcceptOriginal(d)
        const after = readQ9Payload(next)
        const optionChanged =
          (before?.choice?.option_id ?? '') !==
          (after?.choice?.option_id ?? '')
        const rationaleChanged =
          (before?.choice?.rationale ?? '') !==
          (after?.choice?.rationale ?? '')
        const progressed = optionChanged || rationaleChanged
        if (!progressed) return d
        flipped.add(d.id)
        return next
      })
      // No row actually changed — the validator is rejecting
      // something we can't fix from the client (e.g. an unknown
      // panel id or a rule we don't recognise). Surface the
      // original error so the architect can decide.
      if (flipped.size === 0) throw err
      totalRecovered += flipped.size

      // W3.2 (Hardening) — when the retry pushes the
      // ``accept_original`` count above the configured threshold,
      // the backend additionally requires a non-empty
      // ``batch_rationale`` on at least one decision's payload.
      // Without this injection the retry would 422 again with
      // "bulk accept of N violations requires batch_rationale".
      // The backend (api/routes/gates.py) scans every decision
      // payload for ``batch_rationale`` so we attach it to the
      // first decision — order-independent.
      const acceptOriginalCount = attempt.filter((d) => {
        const q9 = readQ9Payload(d)
        return q9?.choice?.option_id === 'accept_original'
      }).length
      if (acceptOriginalCount > bulkAcceptThreshold) {
        const firstIdx = attempt.findIndex(
          (d) => d.payload && typeof d.payload === 'object',
        )
        if (firstIdx >= 0) {
          const target = attempt[firstIdx]
          const existing = (target.payload as Record<string, unknown>) ?? {}
          const existingBatch = (existing as { batch_rationale?: unknown })
            .batch_rationale
          if (
            typeof existingBatch !== 'string' ||
            existingBatch.trim().length === 0
          ) {
            attempt = attempt.map((d, i) =>
              i === firstIdx
                ? {
                    ...d,
                    payload: {
                      ...existing,
                      batch_rationale: BULK_BATCH_RATIONALE,
                    },
                  }
                : d,
            )
          }
        }
      }
    }
  }
  // Shouldn't reach here — the loop either returns or throws — but
  // keep the type checker happy.
  return { updated: attempt, totalRecovered }
}
